<template>
  <section class="sp-panel sp-reader" aria-label="Resource details">
    <button type="button" class="sp-link" @click="$emit('close')">
      ← Back to resources
    </button>
    <portal-feedback
      :loading="loading"
      :error="error"
      :retry="!resource"
      :success="success"
      @retry="load"
    />
    <template v-if="resource">
      <div class="sp-row sp-between">
        <div>
          <p class="sp-eyebrow">
            {{ resource.category }} · Version {{ resource.version }}
          </p>
          <h2>{{ resource.title }}</h2>
        </div>
        <span v-if="resource.archived" class="sp-badge">Archived</span>
      </div>
      <p class="sp-muted">Published {{ displayDate(resource.publishedAt) }}</p>
      <p>{{ resource.summary }}</p>
      <div
        v-for="(section, index) in resource.content || []"
        :key="index"
        class="sp-policy-section"
      >
        <h3 v-if="section.heading">{{ section.heading }}</h3>
        <p
          v-for="(paragraph, n) in section.paragraphs"
          :key="n"
          class="sp-prose"
        >
          {{ paragraph }}
        </p>
      </div>
      <p v-if="resource.url && resourceUrl(resource.url)">
        <a
          class="sp-link"
          :href="resourceUrl(resource.url)"
          target="_blank"
          rel="noopener noreferrer"
          >Open official resource ↗</a
        >
      </p>
      <p v-if="resource.downloadUrl && resourceUrl(resource.downloadUrl)">
        <a class="sp-link" :href="resourceUrl(resource.downloadUrl)" download
          >Download document</a
        >
      </p>
      <section v-if="resource.acknowledgmentRequired" class="sp-notice">
        <h3>Acknowledgment of this version</h3>
        <p
          v-for="(line, index) in resource.acknowledgementText || []"
          :key="index"
          class="sp-prose"
        >
          {{ line }}
        </p>
        <p v-if="resource.acknowledgedAt" class="sp-success-text">
          Acknowledged {{ displayDate(resource.acknowledgedAt) }}
        </p>
        <template v-else-if="mayAcknowledge && !resource.archived">
          <label class="sp-check"
            ><input v-model="confirmed" type="checkbox" :disabled="busy" /> I
            have read this resource and confirm the acknowledgment above for
            version {{ resource.version }}.</label
          >
          <oxd-button
            label="Acknowledge this version"
            display-type="secondary"
            :disabled="!confirmed || busy"
            @click="acknowledge"
          />
        </template>
        <p v-else-if="!resource.archived" class="sp-muted">
          A linked staff account is required to acknowledge this resource.
        </p>
      </section>
      <div v-if="isAdmin && !resource.archived" class="sp-actions">
        <button
          class="sp-link sp-danger-text"
          type="button"
          :disabled="busy"
          @click="archive"
        >
          Archive this version
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import {computed, ref, watch} from 'vue';
import PortalFeedback from './PortalFeedback.vue';
import {portalApi, displayDate, resourceUrl, errorMessage} from '../portal';

export default {
  components: {PortalFeedback},
  props: {
    resourceId: {type: Number, required: true},
    canAcknowledge: Boolean,
    isAdmin: Boolean,
  },
  emits: ['close', 'changed'],
  setup(props, {emit}) {
    const api = portalApi('resources');
    const resource = ref(null);
    const loading = ref(false);
    const busy = ref(false);
    const error = ref('');
    const success = ref('');
    const confirmed = ref(false);
    const mayAcknowledge = computed(() =>
      typeof resource.value?.canAcknowledge === 'boolean'
        ? resource.value.canAcknowledge
        : props.canAcknowledge,
    );
    let generation = 0;
    async function load() {
      const request = ++generation;
      loading.value = true;
      error.value = '';
      success.value = '';
      resource.value = null;
      confirmed.value = false;
      try {
        const data = await api.get(props.resourceId);
        if (request === generation) resource.value = data;
      } catch (e) {
        if (request === generation) error.value = errorMessage(e);
      } finally {
        if (request === generation) loading.value = false;
      }
    }
    async function act(payload, message) {
      if (busy.value) return;
      busy.value = true;
      error.value = '';
      try {
        resource.value = await api.act(resource.value.id, payload);
        confirmed.value = false;
        success.value = message;
        emit('changed');
      } catch (e) {
        error.value = errorMessage(e);
      } finally {
        busy.value = false;
      }
    }
    function acknowledge() {
      if (
        !confirmed.value ||
        !mayAcknowledge.value ||
        resource.value?.archived ||
        resource.value?.acknowledgedAt
      )
        return;
      return act(
        {
          action: 'acknowledge',
          contentHash: resource.value.contentHash,
          confirmed: true,
        },
        'Your acknowledgment has been recorded for this version.',
      );
    }
    function archive() {
      if (
        !props.isAdmin ||
        !window.confirm(
          'Archive this published version? It will no longer appear in the current resource list.',
        )
      )
        return;
      return act(
        {action: 'archive'},
        'This resource version has been archived.',
      );
    }
    watch(() => props.resourceId, load, {immediate: true});
    return {
      resource,
      loading,
      busy,
      error,
      success,
      confirmed,
      mayAcknowledge,
      displayDate,
      resourceUrl,
      load,
      acknowledge,
      archive,
    };
  },
};
</script>
